<script setup lang="ts">
// Page admin V2 — Export des lignes de devis par company (super-admin uniquement).
// Accès : route /admin/exports-devis (meta.requiresSuperAdmin). Design : DaisyUI (cf. design-system/MASTER.md).
// Flux : saisir l'UUID company → synchroniser → filtrer par dates (date_order) → cocher/décocher →
//        aperçu des lignes produit des devis confirmés (sale/done) → export CSV.
import { computed, ref } from 'vue'
import {
  ArrowPathIcon,
  TableCellsIcon,
  ArrowDownTrayIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/vue/24/outline'
import { useQuotesExportAdminStore, type QuotesSyncSummary } from '@/v2/stores/useQuotesExportAdminStore'

const store = useQuotesExportAdminStore()

// ── Synchronisation ──
const companyUuid = ref('')
const syncResult = ref<QuotesSyncSummary | null>(null)
const syncError = ref('')

// ── Filtre / sélection ──
const dateFrom = ref('')
const dateTo = ref('')
const loadError = ref('')
const selected = ref<Record<string, boolean>>({})
const hasLoadedQuotes = ref(false)

// ── Aperçu ──
const showPreview = ref(false)

const rangeInvalid = computed(() => !!dateFrom.value && !!dateTo.value && dateFrom.value > dateTo.value)

const allSelected = computed<boolean>({
  get: () => store.quotes.length > 0 && store.quotes.every((q) => selected.value[q.uuid]),
  set: (val) => {
    for (const q of store.quotes) selected.value[q.uuid] = val
  },
})

const selectedUuids = computed(() => store.quotes.filter((q) => selected.value[q.uuid]).map((q) => q.uuid))

const totalQuantity = computed(() => store.lines.reduce((s, l) => s + (l.quantity ?? 0), 0))
const totalAmount = computed(() => store.lines.reduce((s, l) => s + (l.priceSubtotal ?? 0), 0))

// ── Vue d'agrégation : par devis (lignes) ou par produit (regroupé) ──
type ViewMode = 'quote' | 'product'
const viewMode = ref<ViewMode>('quote')

interface ProductQuoteContribution {
  quoteReference: string
  quantity: number
  amount: number
}
interface ProductGroup {
  key: string
  productName: string
  productCategory: string | null
  totalQuantity: number
  totalAmount: number
  avgUnitPrice: number
  quotes: ProductQuoteContribution[]
}

const groupedByProduct = computed<ProductGroup[]>(() => {
  const groups = new Map<string, { g: ProductGroup; quotes: Map<string, ProductQuoteContribution> }>()
  for (const l of store.lines) {
    const key = l.productOdooId != null ? `id:${l.productOdooId}` : `name:${l.productName}`
    let entry = groups.get(key)
    if (!entry) {
      entry = {
        g: { key, productName: l.productName, productCategory: l.productCategory ?? null, totalQuantity: 0, totalAmount: 0, avgUnitPrice: 0, quotes: [] },
        quotes: new Map(),
      }
      groups.set(key, entry)
    }
    entry.g.totalQuantity += l.quantity ?? 0
    entry.g.totalAmount += l.priceSubtotal ?? 0
    if (!entry.g.productCategory && l.productCategory) entry.g.productCategory = l.productCategory
    const qc = entry.quotes.get(l.quoteReference) ?? { quoteReference: l.quoteReference, quantity: 0, amount: 0 }
    qc.quantity += l.quantity ?? 0
    qc.amount += l.priceSubtotal ?? 0
    entry.quotes.set(l.quoteReference, qc)
  }
  return [...groups.values()]
    .map(({ g, quotes }) => ({
      ...g,
      avgUnitPrice: g.totalQuantity ? g.totalAmount / g.totalQuantity : 0,
      quotes: [...quotes.values()].sort((a, b) => b.amount - a.amount),
    }))
    .sort((a, b) => b.totalAmount - a.totalAmount)
})

async function sync() {
  if (!companyUuid.value.trim() || store.isSyncing) return
  syncError.value = ''
  syncResult.value = null
  const res = await store.syncCompany(companyUuid.value.trim())
  if (res.ok && res.data) {
    syncResult.value = res.data
    await loadQuotes() // rafraîchit la liste des devis confirmés après la synchro
  } else {
    syncError.value = res.message ?? 'Échec de la synchronisation'
  }
}

async function loadQuotes() {
  if (!companyUuid.value.trim()) {
    loadError.value = "Renseignez l'UUID de la company."
    return
  }
  if (rangeInvalid.value) return
  loadError.value = ''
  showPreview.value = false
  await store.fetchLines([]) // réinitialise l'aperçu
  const list = await store.fetchConfirmed(
    companyUuid.value.trim(),
    dateFrom.value || undefined,
    dateTo.value || undefined,
  )
  const map: Record<string, boolean> = {}
  for (const q of list) map[q.uuid] = true // tout sélectionné par défaut
  selected.value = map
  hasLoadedQuotes.value = true
}

async function preparePreview() {
  if (selectedUuids.value.length === 0) return
  showPreview.value = true
  await store.fetchLines(selectedUuids.value)
}

function exportCsv() {
  if (store.lines.length === 0) return
  const sep = ';'
  const esc = (v: string) => (/[";\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v)
  const num = (n: number | null | undefined) => (n == null ? '' : String(n).replace('.', ','))

  let header: string[]
  let body: string[]
  let totalRow: string[]

  if (viewMode.value === 'product') {
    header = ['Produit', 'Catégorie', 'Unités', 'Prix total HT', 'Prix moyen HT', 'Devis']
    body = groupedByProduct.value.map((g) =>
      [
        esc(g.productName),
        esc(g.productCategory ?? ''),
        num(g.totalQuantity),
        num(g.totalAmount),
        num(g.avgUnitPrice),
        esc(g.quotes.map((q) => `${q.quoteReference} (${formatEur(q.amount)})`).join(' | ')),
      ].join(sep),
    )
    totalRow = [`TOTAL (${groupedByProduct.value.length} produits)`, '', num(totalQuantity.value), num(totalAmount.value), '', '']
  } else {
    header = ['Référence devis', 'Produit', 'Catégorie', 'Quantité', 'Prix unitaire HT', 'Prix total HT']
    body = store.lines.map((l) =>
      [
        esc(l.quoteReference),
        esc(l.productName),
        esc(l.productCategory ?? ''),
        num(l.quantity),
        num(l.priceUnit),
        num(l.priceSubtotal),
      ].join(sep),
    )
    totalRow = ['', `TOTAL (${store.lines.length} produits)`, '', num(totalQuantity.value), '', num(totalAmount.value)]
  }

  const csv = [header.join(sep), ...body, totalRow.join(sep)].join('\r\n')
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' }) // BOM pour Excel/accents
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `devis-${viewMode.value === 'product' ? 'produits' : 'lignes'}-${companyUuid.value.trim() || 'company'}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// Format DD/MM/YYYY sans new Date() (la valeur est un ISO 'YYYY-MM-DD...').
function formatDay(iso: string | null): string {
  if (!iso) return '—'
  const [y, m, d] = iso.slice(0, 10).split('-')
  return d && m && y ? `${d}/${m}/${y}` : '—'
}

const eur = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' })
function formatEur(n: number | null | undefined): string {
  return eur.format(n ?? 0)
}
</script>

<template>
  <div class="mx-auto w-full max-w-7xl p-4 md:p-8">
    <header>
      <h1 class="text-2xl font-bold">Export devis (lignes)</h1>
      <p class="mt-1 max-w-prose text-base-content/80">
        Synchronisez les devis d'une company depuis Odoo, filtrez les devis confirmés par période, puis
        prévisualisez et exportez le détail des lignes produit.
      </p>
    </header>

    <!-- 1. Synchronisation -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="sync-title">
      <div class="card-body p-4 md:p-6">
        <h2 id="sync-title" class="card-title text-lg font-semibold">1. Synchroniser les devis</h2>
        <p class="max-w-prose text-base-content/80">
          Relit les devis (et leurs lignes) de la company depuis Odoo et les met à jour en base.
        </p>

        <form class="mt-3 flex flex-wrap items-end gap-4" @submit.prevent="sync">
          <div class="grow">
            <label class="label" for="company-uuid"><span class="label-text">UUID de la company</span></label>
            <input
              id="company-uuid"
              v-model="companyUuid"
              type="text"
              class="input input-bordered w-full max-w-md font-mono"
              placeholder="00000000-0000-0000-0000-000000000000"
              autocomplete="off"
            />
          </div>
          <button type="submit" class="btn btn-primary" :disabled="!companyUuid.trim() || store.isSyncing" :aria-busy="store.isSyncing">
            <span v-if="store.isSyncing" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            <ArrowPathIcon v-else class="h-5 w-5" aria-hidden="true" />
            {{ store.isSyncing ? 'Synchronisation…' : 'Synchroniser' }}
          </button>
        </form>

        <div v-if="syncError" role="alert" class="alert alert-error mt-3">
          <ExclamationTriangleIcon class="h-5 w-5" aria-hidden="true" />
          <span>{{ syncError }}</span>
        </div>

        <div v-if="syncResult" role="status" class="mt-4 rounded-box border border-base-300 bg-base-200 p-4">
          <h3 class="flex items-center gap-2 font-semibold">
            <CheckCircleIcon class="h-5 w-5 text-success" aria-hidden="true" />
            Synchronisation terminée
          </h3>
          <ul class="mt-2 flex flex-wrap gap-2 text-base-content/80">
            <li class="badge badge-neutral tabular-nums">Créés : {{ syncResult.created }}</li>
            <li class="badge badge-neutral tabular-nums">Mis à jour : {{ syncResult.updated }}</li>
            <li class="badge badge-ghost tabular-nums">Inchangés : {{ syncResult.unchanged }}</li>
            <li class="badge badge-neutral tabular-nums">Lignes synchronisées : {{ syncResult.linesSynced }}</li>
            <li v-if="syncResult.errors.length" class="badge badge-error tabular-nums">Erreurs : {{ syncResult.errors.length }}</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- 2. Devis confirmés -->
    <section class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="quotes-title">
      <div class="card-body p-4 md:p-6">
        <h2 id="quotes-title" class="card-title text-lg font-semibold">2. Devis confirmés</h2>
        <p class="max-w-prose text-base-content/80">
          Devis aux états Odoo « sale » et « done », filtrés sur la date du devis (date_order).
        </p>

        <form class="mt-3 flex flex-wrap items-end gap-4" @submit.prevent="loadQuotes">
          <div>
            <label class="label" for="date-from"><span class="label-text">Du</span></label>
            <input id="date-from" v-model="dateFrom" type="date" class="input input-bordered tabular-nums" :class="{ 'input-error': rangeInvalid }" />
          </div>
          <div>
            <label class="label" for="date-to"><span class="label-text">Au</span></label>
            <input id="date-to" v-model="dateTo" type="date" class="input input-bordered tabular-nums" :class="{ 'input-error': rangeInvalid }" />
          </div>
          <button type="submit" class="btn btn-primary" :disabled="!companyUuid.trim() || rangeInvalid || store.isLoadingQuotes" :aria-busy="store.isLoadingQuotes">
            <span v-if="store.isLoadingQuotes" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            {{ store.isLoadingQuotes ? 'Chargement…' : 'Charger les devis' }}
          </button>
        </form>

        <p v-if="rangeInvalid" class="mt-1 text-sm text-error">La date de début doit être antérieure ou égale à la date de fin.</p>
        <p v-if="loadError" class="mt-1 text-sm text-error">{{ loadError }}</p>

        <div v-if="store.quotes.length" class="-mx-4 mt-3 overflow-x-auto md:mx-0">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">
                  <label class="flex cursor-pointer items-center gap-2">
                    <input v-model="allSelected" type="checkbox" class="checkbox checkbox-sm" />
                    <span class="sr-only">Tout sélectionner</span>
                  </label>
                </th>
                <th scope="col">Référence</th>
                <th scope="col">Date</th>
                <th scope="col" class="text-right">Total HT (lignes)</th>
                <th scope="col" class="text-right">Total devis TTC</th>
                <th scope="col" class="text-right">Lignes</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="q in store.quotes" :key="q.uuid">
                <td>
                  <label class="flex cursor-pointer items-center gap-2">
                    <input v-model="selected[q.uuid]" type="checkbox" class="checkbox checkbox-sm" />
                    <span class="sr-only">Sélectionner {{ q.quoteReference }}</span>
                  </label>
                </td>
                <td class="font-medium">
                  {{ q.quoteReference }}
                  <span v-if="q.customerReference" class="block text-sm text-base-content/70">{{ q.customerReference }}</span>
                </td>
                <td class="whitespace-nowrap tabular-nums">{{ formatDay(q.dateOrder) }}</td>
                <td class="text-right tabular-nums">{{ formatEur(q.linesSubtotal) }}</td>
                <td class="text-right tabular-nums">{{ formatEur(q.totalCa) }}</td>
                <td class="text-right tabular-nums">{{ q.lineCount }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p v-else-if="hasLoadedQuotes && !store.isLoadingQuotes" class="mt-3 text-base-content/80">
          Aucun devis confirmé trouvé pour cette company sur la période.
        </p>

        <div v-if="store.quotes.length" class="mt-4 flex flex-wrap items-center gap-3">
          <button type="button" class="btn btn-primary" :disabled="selectedUuids.length === 0 || store.isLoadingLines" :aria-busy="store.isLoadingLines" @click="preparePreview">
            <span v-if="store.isLoadingLines" class="loading loading-spinner loading-xs" aria-hidden="true"></span>
            <TableCellsIcon v-else class="h-5 w-5" aria-hidden="true" />
            Aperçu / Export ({{ selectedUuids.length }})
          </button>
        </div>
      </div>
    </section>

    <!-- 3. Aperçu -->
    <section v-if="showPreview" class="card mt-6 border border-base-300 bg-base-100 shadow-sm" aria-labelledby="preview-title">
      <div class="card-body p-4 md:p-6">
        <div class="flex flex-wrap items-center justify-between gap-3">
          <h2 id="preview-title" class="card-title text-lg font-semibold">3. Aperçu</h2>
          <div class="flex flex-wrap items-center gap-3">
            <div class="join" role="group" aria-label="Mode d'affichage">
              <button
                type="button"
                class="btn join-item"
                :class="viewMode === 'quote' ? 'btn-primary' : 'btn-ghost'"
                :aria-pressed="viewMode === 'quote'"
                @click="viewMode = 'quote'"
              >
                Par devis
              </button>
              <button
                type="button"
                class="btn join-item"
                :class="viewMode === 'product' ? 'btn-primary' : 'btn-ghost'"
                :aria-pressed="viewMode === 'product'"
                @click="viewMode = 'product'"
              >
                Par produit
              </button>
            </div>
            <button type="button" class="btn btn-ghost" :disabled="store.lines.length === 0" @click="exportCsv">
              <ArrowDownTrayIcon class="h-5 w-5" aria-hidden="true" />
              Exporter CSV
            </button>
          </div>
        </div>

        <!-- Vue par devis (lignes détaillées) -->
        <div v-if="store.lines.length && viewMode === 'quote'" class="-mx-4 mt-3 overflow-x-auto md:mx-0">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Référence devis</th>
                <th scope="col">Produit</th>
                <th scope="col">Catégorie</th>
                <th scope="col" class="text-right">Quantité</th>
                <th scope="col" class="text-right">Prix unitaire HT</th>
                <th scope="col" class="text-right">Prix total HT</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="l in store.lines" :key="l.uuid">
                <td class="whitespace-nowrap font-medium">{{ l.quoteReference }}</td>
                <td>{{ l.productName }}</td>
                <td>{{ l.productCategory ?? '—' }}</td>
                <td class="text-right tabular-nums">{{ l.quantity }}</td>
                <td class="text-right tabular-nums">{{ formatEur(l.priceUnit) }}</td>
                <td class="text-right tabular-nums">{{ formatEur(l.priceSubtotal) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-semibold">
                <td colspan="3">Total — {{ store.lines.length }} produits</td>
                <td class="text-right tabular-nums">{{ totalQuantity }}</td>
                <td></td>
                <td class="text-right tabular-nums">{{ formatEur(totalAmount) }}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        <!-- Vue par produit (agrégée) -->
        <div v-else-if="store.lines.length && viewMode === 'product'" class="-mx-4 mt-3 overflow-x-auto md:mx-0">
          <table class="table">
            <thead>
              <tr>
                <th scope="col">Produit</th>
                <th scope="col">Catégorie</th>
                <th scope="col" class="text-right">Unités</th>
                <th scope="col" class="text-right">Prix total HT</th>
                <th scope="col" class="text-right">Prix moyen HT</th>
                <th scope="col">Devis</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="g in groupedByProduct" :key="g.key">
                <td class="font-medium">{{ g.productName }}</td>
                <td>{{ g.productCategory ?? '—' }}</td>
                <td class="text-right tabular-nums">{{ g.totalQuantity }}</td>
                <td class="text-right tabular-nums">{{ formatEur(g.totalAmount) }}</td>
                <td class="text-right tabular-nums">{{ formatEur(g.avgUnitPrice) }}</td>
                <td>
                  <details>
                    <summary class="cursor-pointer">{{ g.quotes.length }} devis</summary>
                    <ul role="list" class="mt-1 space-y-0.5">
                      <li v-for="q in g.quotes" :key="q.quoteReference" class="text-sm tabular-nums">
                        {{ q.quoteReference }} — {{ formatEur(q.amount) }} ({{ q.quantity }} u.)
                      </li>
                    </ul>
                  </details>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="font-semibold">
                <td colspan="2">Total — {{ groupedByProduct.length }} produits</td>
                <td class="text-right tabular-nums">{{ totalQuantity }}</td>
                <td class="text-right tabular-nums">{{ formatEur(totalAmount) }}</td>
                <td></td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>

        <p v-else-if="!store.isLoadingLines" class="mt-3 text-base-content/80">
          Aucune ligne produit pour les devis sélectionnés. Lancez une synchronisation si nécessaire.
        </p>
      </div>
    </section>
  </div>
</template>
